/** @jsx isolateComponent */
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { css } from '@emotion/react';
import { Colors } from '../../../../theme/Colors';
import { CTATextContent } from '../CTATextContent';
import { BgColor, DeprecatedBgColor, CTABtnColor } from '../types';
import { getBgColor } from '../utils';
import type { AnchorTarget } from '../../../../ts-types';

export type Props = {
  /**
   * Sets the container's background color, must be one of type `'lava60' | 'ocean40' | 'sun60' | 'white'
   * | 'zapier' | 'night' | 'moss' | 'neutral800' | 'neutral100' | 'earth'`.
   */
  backgroundColor?: BgColor | DeprecatedBgColor;
  /**
   * max-width in pixels applied to the content area of container.
   */
  maxWidth?: number;
  /**
   * Heading text content.
   */
  heading: React.ReactNode;
  /**
   * Text content displayed below the heading.
   */
  subHeading?: React.ReactNode;
  /**
   * Text content displayed below the sub-heading.
   */
  description?: React.ReactNode;
  /**
   * Text content of the CTA button.
   */
  ctaText?: React.ReactNode;
  /**
   * href for the CTA button.
   */
  ctaHref?: string;
  /**
   * Color of the CTA button.
   *
   * Must be one of the type `'primary' | 'primary-alt' | 'primary-dark' | 'brand'`
   */
  ctaBtnColor?: CTABtnColor;
  /**
   * CTA button click handler.
   */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * CTA button keyDown handler.
   */
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  /**
   * Renders in the space opposite the text.
   * Can be used to render media such as images, videos, or any other React Component.
   */
  children?: React.ReactNode;
  /**
   * The `target` attribute for the CTA button.
   */
  ctaTarget?: AnchorTarget;
};

const Styles = labelStyles('CTAFilled', {
  root: (
    backgroundColor: DeprecatedBgColor | BgColor,
    maxWidth: number = 1170
  ) => css`
    display: grid;
    align-items: center;
    grid-template-columns: repeat(1, 1fr);
    gap: 40px;
    max-width: ${maxWidth}px;
    margin: 20px;
    padding: 40px 30px;
    border-radius: 10px;
    background-color: ${Colors[backgroundColor]};

    @media screen and (min-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
      margin: 40px 30px;
      padding: 40px;
    }

    @media screen and (min-width: 1230px) {
      margin: 40px auto;
      padding: 40px 70px;
    }
  `,
  mediaContent: css`
    @media screen and (min-width: 768px) {
      margin: 0 auto;
    }
  `,
});

export const CTAFilled = ({
  backgroundColor = 'neutral100',
  children,
  ctaHref,
  ctaBtnColor,
  ctaText,
  description,
  heading,
  maxWidth,
  onCtaClick,
  onCtaKeyDown,
  subHeading,
  ctaTarget,
}: Props) => {
  const bgColor = getBgColor(backgroundColor);

  return (
    <div css={Styles.root(bgColor, maxWidth)}>
      <CTATextContent
        heading={heading}
        subHeading={subHeading}
        description={description}
        ctaText={ctaText}
        ctaHref={ctaHref}
        ctaBtnColor={ctaBtnColor}
        onCtaClick={onCtaClick}
        onCtaKeyDown={onCtaKeyDown}
        bgColor={bgColor}
        ctaTarget={ctaTarget}
      />
      <div css={Styles.mediaContent} data-testid="media-content">
        {children}
      </div>
    </div>
  );
};
